import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDepartmentColumn } from 'src/assets/static/view_department/useDepartmentColumn';
import useCompany from 'src/hooks/useCompany';
import useViewDepartment from 'src/hooks/useViewDepartments';
import NestedExcelDropdown, { Category } from 'src/sections/view_checkbook/NestedExcelDropdown';
import { formatGLCodesExcelData, formatMembersExcelData } from 'src/sections/view_checkbook/index';
import { DepartmentResponse } from 'src/services/useGetDepartmentApi';
import exportToExcel from 'src/utils/exportToExcel';
import { sliceFiveChar } from 'src/utils/strings';
import AddNewDepartment from './AddNewDepartment';
import BulkUpdateGLCode from './BulkUpdateGLCode';
import BulkUpdateMember from './BulkUpdateMember';
import ClearFilters from './ClearFilters';
import CopyDepartment from './CopyDepartment';
import DeleteDepartment from './DeleteDepartment';

const departmentOptions: Category[] = [
  {
    content: 'GL Codes',
    id: 1,
    items: [
      { content: 'Current Page', id: 2 },
      { content: 'All Pages', id: 3 },
    ],
    parent: true,
  },
  {
    content: 'Members',
    id: 4,
    items: [
      { content: 'Current Page', id: 5 },
      { content: 'All Pages', id: 6 },
    ],
    parent: true,
  },
];

function ViewDepartmentTableToolbar() {
  const {
    departmentTableData,
    departments,
    setExcelLoading,
    hasEditDepartmentPermission,
    buId,
    departmentsDataState,
    refreshTable,
  } = useViewDepartment();

  const {
    company: { companyName },
  } = useCompany();

  const selectedRows = useMemo(
    () => departmentTableData.filter((item) => item.selected),
    [departmentTableData]
  );

  const allDepartments = useMemo(
    () => orderBy(departments, departmentsDataState?.sort as SortDescriptor[]),
    [departments, departmentsDataState]
  );
  const { t } = useTranslation();
  const viewDepartmentColumns = useDepartmentColumn();

  const viewDepartmentExcelColumns = useMemo(
    () =>
      viewDepartmentColumns.filter(
        (item) =>
          !['selected', 'departments', 'glCodes', 'members', 'canEdit'].includes(item?.field || '')
      ),
    [viewDepartmentColumns]
  );

  const excelExport = useCallback(
    (data: DepartmentResponse[], isGLCodes?: boolean) => {
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      const departmentColumns = [
        { field: 'companyName', title: t('Company Name') },
        ...viewDepartmentExcelColumns,
        ...(isGLCodes
          ? [
              { field: 'code', title: t('GL Code') },
              { field: 'description', title: t('Description') },
            ]
          : [{ field: 'memberName', title: t('Member Name') }]),
      ];
      departmentColumns.forEach((item) => {
        columns.push(item.field as string);
        Object.assign(columnMapping, { [item.field as string]: item.title });
      });
      const rows = data.map((item) => ({
        ...item,
      }));

      exportToExcel({
        columns,
        rows,
        fileName: `${buId ? `${buId}_` : ''}${sliceFiveChar(companyName || '') + '_'}${`${
          isGLCodes ? t('GLCodes') : t('Members')
        }_`}${new Date().toLocaleString().replace(/[ ,]+/g, ' ')}`,
        columnMapping,
        sheetName: t('department'),
      });
    },
    [buId, companyName, t, viewDepartmentExcelColumns]
  );

  const handleTreeDropdownExcelChange = useCallback(
    (selectedOption: number) => {
      if (selectedOption === 2) {
        setExcelLoading(true);
        const formattedData = formatGLCodesExcelData(
          departmentTableData.map((row) => ({ ...row, companyName }))
        );
        excelExport(formattedData, true);
        setExcelLoading(false);
      } else if (selectedOption === 3) {
        setExcelLoading(true);
        const formattedData = formatGLCodesExcelData(
          allDepartments.map((row) => ({ ...row, companyName }))
        );
        excelExport(formattedData, true);
        setExcelLoading(false);
      } else if (selectedOption === 5) {
        setExcelLoading(true);
        const formattedData = formatMembersExcelData(
          departmentTableData.map((row) => ({ ...row, companyName }))
        );
        excelExport(formattedData);
        setExcelLoading(false);
      } else if (selectedOption === 6) {
        setExcelLoading(true);
        const formattedData = formatMembersExcelData(
          allDepartments.map((row) => ({ ...row, companyName }))
        );
        excelExport(formattedData);
        setExcelLoading(false);
      }
    },
    [allDepartments, companyName, departmentTableData, excelExport, setExcelLoading]
  );

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-1 text-white text-sm">
        <ClearFilters />
        {hasEditDepartmentPermission && (
          <>
            {t('Click on department name to rename')}
            <AddNewDepartment />
            <DeleteDepartment selectedRows={selectedRows} />
            <CopyDepartment selectedRows={selectedRows} refresh={refreshTable} />
            <BulkUpdateMember />
            <BulkUpdateGLCode />
          </>
        )}
      </div>
      <div className="flex flex-row gap-2">
        <NestedExcelDropdown
          handleExcelChange={handleTreeDropdownExcelChange}
          options={departmentOptions}
        />
      </div>
    </div>
  );
}

export default ViewDepartmentTableToolbar;
